import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { COOKIE_IDS } from 'App/constants';
import { BANK_TRANSFER_TEST_IDS } from 'Pages/rewards/components/bankTransfer/constants';
import {
  DisclaimerContainer,
  ExploreContainer,
  PolicyLink,
  StepperContainer,
  Title
} from 'Pages/rewards/components/bankTransfer/explore/style';
import { BankTransferViews } from 'Pages/rewards/components/bankTransfer/views';
import { Button, ButtonSizes, ButtonWidth, ContextTypes } from 'Shared/components/design/button/Button';
import Stepper, { StepperState, StepperType } from 'Shared/components/design/stepper/Stepper';
import { ICON_IDS } from 'Shared/components/icons/constants';
import { useSetIdempotencyKey } from 'Shared/hooks/useSetIdempotencyKey';

type ExploreProps = {
  redemptionStep         : BankTransferViews;
  redemptionStepCallback : (redemptionStep: BankTransferViews) => void;
  maximumRedeemablePoints: number;
  currentPoints          : number;
}

/**
 * Renders <Explore /> component
 * @param param0
 * @returns
 */
function Explore({redemptionStep, redemptionStepCallback, maximumRedeemablePoints, currentPoints}: ExploreProps): JSX.Element {
  const {t}                        = useTranslation('rewards');
  const DWOLLA_PRIVACY_POLICY_LINK = 'https://www.dwolla.com/legal/privacy/#legal-content';

  // set the idempotency key cookie for the redemption flow start
  useSetIdempotencyKey(true, COOKIE_IDS.REWARDS_IDEMPOTENCY_KEY);

  /**
   * Renders button only if user can start redemption
   * @returns
   */
  const renderButton = (): JSX.Element => {
    if (redemptionStep === BankTransferViews.REDEMPTION_START) {
      return(
        <>
          <Button
            context   = {ContextTypes.PRIMARY}
            size      = {ButtonSizes.LARGE}
            width     = {ButtonWidth.FULL}
            iconRight = {ICON_IDS.ARROW_RIGHT}
            onClick   = {() => redemptionStepCallback(BankTransferViews.SUBMIT_BANK_DETAILS)}
            disabled  = { currentPoints > maximumRedeemablePoints }
          >
            {t('BankTransfer--explore-button-continue')}
          </Button>
          <DisclaimerContainer>
            {t('BankTransfer--disclaimer-text')}
            <PolicyLink href={DWOLLA_PRIVACY_POLICY_LINK} target='_blank'>
              {t('BankTransfer--disclaimer-link')}
            </PolicyLink>
          </DisclaimerContainer>
        </>
      );
    }

    return <></>;
  };

  return(
    <ExploreContainer data-testid={BANK_TRANSFER_TEST_IDS.EXPLORE}>
      <Title>
        <Trans i18nKey = {'BankTransfer--explore-title'}>
          Survey Junkie uses <span>Dwolla</span> to connect your bank
        </Trans>
      </Title>
      <StepperContainer>
        <Stepper
            steps = {[{
              title       : t('BankTransfer--explore-stepper-title-one'),
              description : t('BankTransfer--explore-stepper-description-one'),
              stateStart  : StepperState.DEFAULT,
              typeStart   : StepperType.ICON,
              iconStart   : ICON_IDS.SWAP,
              noBorderIcon: true,
            },{
              title       : t('BankTransfer--explore-stepper-title-two'),
              description : t('BankTransfer--explore-stepper-description-two'),
              stateStart  : StepperState.DEFAULT,
              typeStart   : StepperType.ICON,
              iconStart   : ICON_IDS.VERIFIED_FILL,
              noBorderIcon: true,
            },{
              title       : t('BankTransfer--explore-stepper-title-three'),
              description : t('BankTransfer--explore-stepper-description-three'),
              stateStart  : StepperState.DEFAULT,
              typeStart   : StepperType.ICON,
              iconStart   : ICON_IDS.BANK_OUTLINE,
              noBorderIcon: true,
            }]
          }
          activeStep    = {2}
          animateToStep = {true}
        />
      </StepperContainer>
        {renderButton()}
    </ExploreContainer>
  );
}

export default Explore;
